import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback } from 'react';
import { useController, useForm } from 'react-hook-form';

import { showAlert } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { useCreateGuardianProfile } from '@/features/auth/queries';
import type { SignUpFormValues } from '@/features/auth/schema';
import { signUpSchema } from '@/features/auth/schema';
import { AuthServices } from '@/features/auth/services';
import { setVerificationSentAt } from '@/features/auth/utils/verify-email-throttle';
import { useUnAuthStackNavigation } from '@/hooks';
import { fetchSignInMethodsForEmail } from '@/libs/firebase/auth';
import { sendEmailVerify, signOut } from '@/libs/firebase/auth/auth-helpers';
import { useUiStore } from '@/stores';
import { useShallow } from 'zustand/shallow';

const strings = STRINGS.auth.signUp;

/** Firebase error code thrown by `createUserWithEmailAndPassword` for a taken email. */
const ERROR_EMAIL_ALREADY_IN_USE = 'auth/email-already-in-use';

export function useSignUp() {
  const navigation = useUnAuthStackNavigation();
  const { mutateAsync: createProfile } = useCreateGuardianProfile();
  const showGlobalLoading = useUiStore(useShallow(s => s.showGlobalLoading));
  const hideGlobalLoading = useUiStore(useShallow(s => s.hideGlobalLoading));

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<SignUpFormValues>({
    resolver: yupResolver(signUpSchema),
    // 'onTouched' so field errors (e.g. short password) surface on blur as well as
    // on submit — see PP-281 scenario 2 ("...or moves focus away from the field").
    mode: 'onTouched',
    defaultValues: {
      firstName: '',
      lastName: '',
      // Pre-fill the dial code so the leading flag resolves to US and "+1" shows (matches Figma).
      phone: '+1',
      email: '',
      password: '',
      acceptedTerms: false,
    },
  });

  const {
    field: { value: firstName, onChange: onChangeFirstName, onBlur: onBlurFirstName },
  } = useController({ name: 'firstName', control });

  const {
    field: { value: lastName, onChange: onChangeLastName, onBlur: onBlurLastName },
  } = useController({ name: 'lastName', control });

  const {
    field: { value: phone, onChange: onChangePhone, onBlur: onBlurPhone },
  } = useController({ name: 'phone', control });

  const {
    field: { value: email, onChange: onChangeEmail, onBlur: onBlurEmail },
  } = useController({ name: 'email', control });

  const {
    field: { value: password, onChange: onChangePassword, onBlur: onBlurPassword },
  } = useController({ name: 'password', control });

  const {
    field: { value: acceptedTerms, onChange: onChangeAcceptedTerms },
  } = useController({ name: 'acceptedTerms', control });

  /**
   * The email is already registered. Figure out *how* and show the matching popup
   * (PP-281 scenarios 3/4/5): verified password account, unverified password account,
   * or an account linked to Google/Apple only.
   */
  const showEmailAlreadyInUseAlert = useCallback(
    async (emailAddress: string) => {
      let methods: string[] = [];
      try {
        methods = await fetchSignInMethodsForEmail(emailAddress);
      } catch {
        // Couldn't read providers (e.g. email-enumeration protection) — fall back to
        // the password-account branch below.
      }

      const hasPassword = methods.includes('password');
      const socialProvider = methods.includes('google.com')
        ? 'Google'
        : methods.includes('apple.com')
          ? 'Apple'
          : undefined;

      // Scenario 5: email is linked to a social account only (no password set).
      if (!hasPassword && socialProvider) {
        showAlert({
          title: strings.errorSocialAccountTitle,
          message: strings.errorSocialAccountMessage.replace('{provider}', socialProvider),
          buttons: [{ text: strings.buttonLogin, onPress: () => navigation.popTo('Login') }],
        });
        return;
      }

      // Password account — distinguish verified (scenario 3) vs unverified (scenario 4).
      let emailVerified = true;
      try {
        const account = await AuthServices.lookupUserAccount(emailAddress);
        emailVerified = account.emailVerified;
      } catch {
        // Default to the "already in use" popup if verification state is unknown.
      }

      if (emailVerified) {
        // Scenario 3: email already in use and verified.
        showAlert({
          title: strings.errorEmailInUseTitle,
          message: strings.errorEmailInUseMessage,
          buttons: [
            {
              text: strings.buttonLogin,
              onPress: () => {
                navigation.popTo('Welcome');
                navigation.navigate('Login');
              },
            },
          ],
        });
        return;
      }

      // Scenario 4: email registered but not verified yet.
      showAlert({
        title: strings.errorEmailNotVerifiedTitle,
        message: strings.errorEmailNotVerifiedMessage,
        buttons: [
          { text: strings.buttonLater },
          {
            text: strings.buttonLogin,
            onPress: () => {
              navigation.popTo('Welcome');
              navigation.navigate('Login');
            },
          },
        ],
      });
    },
    [navigation],
  );

  const handleCreateAccount = useCallback(
    async (_data: SignUpFormValues) => {
      try {
        showGlobalLoading();
        await createProfile({
          data: {
            phone: _data.phone,
            firstname: _data.firstName,
            lastname: _data.lastName,
            email: _data.email,
            password: _data.password,
          },
          // Sign-up surfaces its own tailored popups (e.g. email-already-in-use),
          // so suppress the shared server-error alert for this request.
          skipServerErrorAlert: true,
        });
        await sendEmailVerify(_data.email, _data.password);
        // PP-330: start the verification-email cooldown from sign-up so a
        // subsequent login within 1h doesn't re-trigger Firebase.
        setVerificationSentAt(_data.email, Date.now());

        signOut();

        // once the VerifyEmail route is implemented in UnAuthStack.
        navigation.navigate('VerifyEmailLinkSent', { email: _data.email });
      } catch (error) {
        const _err = error as { code?: string; status?: number };
        const code = _err?.code;
        const statusCode = _err?.status;
        if (code === ERROR_EMAIL_ALREADY_IN_USE || statusCode === 409) {
          // Hide the spinner before the provider/verification lookups so the user
          // isn't blocked behind it while we decide which popup to show.
          await showEmailAlreadyInUseAlert(_data.email);
        }

        // captureException({ error_payload: error }, { tags: { usk_log_type: 'sign_up' } });
      } finally {
        hideGlobalLoading();
      }
    },
    [showGlobalLoading, createProfile, navigation, hideGlobalLoading, showEmailAlreadyInUseAlert],
  );

  const handleSubmitForm = handleSubmit(handleCreateAccount);

  const handleBack = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  return {
    firstName,
    onChangeFirstName,
    onBlurFirstName,
    lastName,
    onChangeLastName,
    onBlurLastName,
    phone,
    onChangePhone,
    onBlurPhone,
    email,
    onChangeEmail,
    onBlurEmail,
    password,
    onChangePassword,
    onBlurPassword,
    acceptedTerms,
    onChangeAcceptedTerms,
    errors,
    isValid,
    isSubmitting,
    handleSubmit: handleSubmitForm,
    handleBack,
  };
}
